import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ELoginAs } from '@aqx-core/common/api/auth';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { useAuthLoginAs } from '@aqx-core/common/services/auth';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { useCompanyProfile } from '@aqx-core/common/services/company';
import { ITabItem } from '@aqx-core/common/types/tab';
import { normalizeSelectedLoginTab } from '@aqx-core/common/utils/auth/normalizeSelectedLoginTab';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';

export const useLoginAsTabs = () => {
  const { t } = useTranslation();

  const loginAs = useAuthLoginAs();

  const companyProfile = useCompanyProfile();

  const companyAccess = useMemo(() => validateCompanyAccess(companyProfile), [companyProfile]);

  const {
    hasCompanyLoginConfig,
    hasMT4LiveLoginConfig,
    hasMT4DemoLoginConfig,
    hasMT5LiveLoginConfig,
    hasMT5DemoLoginConfig
  } = companyAccess;

  const tabs = useMemo(() => {
    const list = [];
    const createTab = (value: ELoginAs, label: string, dataTestId: EDataTestId): ITabItem<ELoginAs> => ({
      value,
      label,
      dataTestId
    });

    // Add CRM (LIVE) tab if company login config exists
    if (hasCompanyLoginConfig) {
      list.push(createTab(ELoginAs.LIVE, t('Live'), EDataTestId.TAB_LOGIN_ACCOUNT_TYPE_CRM));
    }

    // Add MT4/MT5 LIVE tab(s)
    const liveLoginType = authenticationViewController.getLiveLoginTypeFromCompanyAccess({
      hasMT4LiveLoginConfig,
      hasMT5LiveLoginConfig
    });

    if (liveLoginType) {
      list.push(createTab(liveLoginType, t('Live'), EDataTestId.TAB_LOGIN_ACCOUNT_TYPE_LIVE));
    }

    // Add DEMO tab if available
    const demoLoginType = authenticationViewController.getDemoLoginTypeFromCompanyAccess({
      hasMT4DemoLoginConfig,
      hasMT5DemoLoginConfig
    });

    if (demoLoginType) {
      list.push(createTab(demoLoginType, t('Demo'), EDataTestId.TAB_LOGIN_ACCOUNT_TYPE_DEMO));
    }

    return list;
  }, [
    hasCompanyLoginConfig,
    hasMT4DemoLoginConfig,
    hasMT4LiveLoginConfig,
    hasMT5DemoLoginConfig,
    hasMT5LiveLoginConfig,
    t
  ]);

  const [selectedTab, setSelectedTab] = useState<ELoginAs>(loginAs ?? tabs[0]?.value);

  useEffect(() => {
    authenticationViewController.setLoginAs(selectedTab);
  }, [selectedTab]);

  return {
    tabs,
    selectedTab: normalizeSelectedLoginTab({ selectedTab, companyAccess }),
    onTabChange: setSelectedTab
  };
};
